import { WithRef } from "@converge-collective/common/models/Base";
import { Network } from "@converge-collective/common/models/Network";
import EventIcon from "@mui/icons-material/Event";
import GroupsIcon from "@mui/icons-material/Groups";
import SportsHandballIcon from "@mui/icons-material/SportsHandball";
import { Alert, Backdrop, Slide, SlideProps, Snackbar } from "@mui/material";
import SpeedDial, { CloseReason } from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { sample } from "lodash";
import React, { useState } from "react";
import LogActivity from "~/components/LogActivity";
import CreateEvent from "~/components/activity/CreateEvent";
import CreateGroup from "./groups/CreateGroup";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(0.5),
      width: 50,
      zIndex: 2000,
    },
    speedDial: {
      position: "absolute",
      top: 15,
    },
    tooltip: {},
    actions: {},
    staticTooltip: {
      // width: 200,
    },
    staticTooltipLabel: {
      width: "auto",
      whiteSpace: "nowrap",
      textAlign: "right",
      background: theme.palette.primary.main,
      color: "white",
    },
  })
);

type TransitionProps = Omit<SlideProps, "direction">;

function TransitionUp(props: TransitionProps) {
  return <Slide {...props} direction="up" />;
}

const messages = ["Way to get after it.", "Get some.", "Nice work."];
const emojis = ["🔥", "💪", "👊", "🙌", "💫", "✨", "⚡", "💥", "🤩", "😤"];
const genMessage = (): string => `${sample(messages)} ${sample(emojis)}`;

export default function AddSpeedDial({
  network,
}: {
  network: WithRef<Network>;
}): React.ReactElement {
  const { settings } = network || {};
  const isManualActivityDisabled = settings?.isManualActivityDisabled === true;
  const classes = useStyles();
  const [openLogActvity, setOpenLogActivity] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [message, setMessage] = useState("");

  const handleSuccClosed = (_?: any, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    console.log("handle succ closed");
    setShowSuccess(false);
  };
  const actions = [
    ...(isManualActivityDisabled
      ? []
      : [
          {
            icon: <SportsHandballIcon color="primary" />,
            name: "Log Activity",
            click: () => setOpenLogActivity(true),
          },
        ]),
    {
      icon: <EventIcon color="primary" />,
      name: "Create Event",
      click: () => setOpenCreateEvent(true),
    },
    {
      icon: <GroupsIcon color="primary" />,
      name: "Create Group",
      click: () => setOpenCreateGroup(true),
    },
  ];

  // NOTE: if you set this to true it'll try to render server side and error
  // with something like "Cannot read properties of undefined (reading
  // 'firestore')"
  const [openCreateGroup, setOpenCreateGroup] = useState(false);
  const handleCreateGroupClose = () => setOpenCreateGroup(false);

  // TODO set back to false
  const [openCreateEvent, setOpenCreateEvent] = useState(false);
  const handleCreateEventClose = () => setOpenCreateEvent(false);
  const handleEventCreated = () => {
    setMessage(`Event created! ${sample(emojis)}`);
    setShowSuccess(true);
  };

  const handleLogActivityClose = () => {
    setOpenLogActivity(false);
  };
  const handleActivityAdded = () => {
    console.log("activity added!");
    setMessage(`Activity added! ${genMessage()}`);
    setShowSuccess(true);
  };

  const toggleOpen = () => {
    setOpen(!open);
  };

  const handleClose = (
    _event: React.SyntheticEvent<HTMLElement>,
    reason: CloseReason
  ) => {
    // ignore mouseLeave
    if (reason !== "mouseLeave") setOpen(false);
  };

  return (
    <>
      <div className={classes.root}>
        <Backdrop open={open} />
        <SpeedDial
          FabProps={{ size: "small", sx: { boxShadow: "none" } }}
          ariaLabel="Log Activity or Create Event"
          className={classes.speedDial}
          hidden={false}
          icon={<SpeedDialIcon />}
          onClose={handleClose}
          onClick={toggleOpen}
          open={open}
          direction="down"
        >
          {actions.map((action) => (
            <SpeedDialAction
              TooltipClasses={classes}
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              classes={{ staticTooltipLabel: classes.staticTooltipLabel }}
              tooltipOpen
              onClick={() => {
                setOpen(false);
                action.click();
              }}
            />
          ))}
        </SpeedDial>
      </div>

      <LogActivity
        isOpen={openLogActvity}
        handleClose={handleLogActivityClose}
        handleActivityAdded={handleActivityAdded}
      />

      <CreateEvent
        network={network}
        isOpen={openCreateEvent}
        handleClose={handleCreateEventClose}
        handleEventCreated={handleEventCreated}
      />

      <CreateGroup
        network={network}
        isOpen={openCreateGroup}
        handleClose={handleCreateGroupClose}
        handleGroupCreated={() => {
          console.log("group created!");
        }}
      />

      <Snackbar
        TransitionComponent={TransitionUp}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={handleSuccClosed}
        open={showSuccess}
        autoHideDuration={3000}
      >
        <Alert severity="info">{message}</Alert>
      </Snackbar>
    </>
  );
}
